import HttpService from "../services/http";
import { SecureStorage } from "./secureStorage";

const ERROR_REPORT_SOURCE = "sum-frontend";
const ERROR_REPORT_VERSION = process.env.REACT_APP_VERSION || "unknown";

interface ErrorReport {
  message: string;
  stack?: string;
  componentStack?: string;
  type: "runtime" | "unhandled-rejection" | "react" | "manual";
  url: string;
  userAgent: string;
  timestamp: string;
  context?: Record<string, unknown>;
  userIdentification?: string | null;
  entityIdentification?: string | null;
}

const generateErrorSignature = (
  errorReport: ErrorReport,
  timestamp: string
): string => {
  const payload = `${ERROR_REPORT_SOURCE}:${ERROR_REPORT_VERSION}:${
    errorReport.type
  }:${errorReport.message.slice(0, 50)}:${timestamp}`;
  return btoa(payload).slice(0, 32);
};

const reportedErrors = new Map<string, number>();
const ERROR_CACHE_TTL = 60000;
const MAX_CACHE_SIZE = 100;

const THIRD_PARTY_STACK_PATTERNS = [
  "chrome-extension://",
  "moz-extension://",
  "safari-extension://",
  "safari-web-extension://",
  "ms-browser-extension://",
  "iabjs://",
  "JSBridgeCallback",
  "webkit-masked-url://",
  "googletagmanager.com",
  "google-analytics.com",
  "googleadservices.com",
  "doubleclick.net",
  "facebook.net",
  "fbcdn.net",
];

const INJECTED_SCRIPT_PATTERNS = [
  "sendDataToNative",
  "sendPageHideMessage",
  "webkit.messageHandlers",
];

const isThirdPartyError = (stack?: string, message?: string): boolean => {
  if (stack && THIRD_PARTY_STACK_PATTERNS.some((p) => stack.includes(p))) {
    return true;
  }

  const haystack = `${stack || ""}${message || ""}`;
  if (!haystack) return false;
  return INJECTED_SCRIPT_PATTERNS.some((p) => haystack.includes(p));
};

const getErrorKey = (message: string, stack?: string): string => {
  return `${message}:${stack?.slice(0, 200) || ""}`;
};

const cleanupExpiredErrors = (): void => {
  const now = Date.now();
  const keysToDelete: string[] = [];

  reportedErrors.forEach((timestamp, key) => {
    if (now - timestamp > ERROR_CACHE_TTL) {
      keysToDelete.push(key);
    }
  });

  keysToDelete.forEach((key) => reportedErrors.delete(key));
};

const wasRecentlyReported = (errorKey: string): boolean => {
  const timestamp = reportedErrors.get(errorKey);
  if (!timestamp) return false;

  if (Date.now() - timestamp > ERROR_CACHE_TTL) {
    reportedErrors.delete(errorKey);
    return false;
  }
  return true;
};

const markAsReported = (errorKey: string): void => {
  if (reportedErrors.size >= MAX_CACHE_SIZE) {
    cleanupExpiredErrors();
  }

  reportedErrors.set(errorKey, Date.now());
};

const sendErrorReport = async (errorReport: ErrorReport): Promise<void> => {
  if (isThirdPartyError(errorReport.stack, errorReport.message)) {
    return;
  }

  const errorKey = getErrorKey(errorReport.message, errorReport.stack);

  if (wasRecentlyReported(errorKey)) {
    return;
  }

  markAsReported(errorKey);

  const signature = generateErrorSignature(errorReport, errorReport.timestamp);

  const reportWithIdentity: ErrorReport = {
    ...errorReport,
    userIdentification: SecureStorage.getUserIdentification(),
    entityIdentification: SecureStorage.getEntityIdentification(),
  };

  try {
    await HttpService.post(
      "/ui/bug-report",
      {
        error: reportWithIdentity,
      },
      {
        headers: {
          "X-Error-Report-Source": ERROR_REPORT_SOURCE,
          "X-Error-Report-Version": ERROR_REPORT_VERSION,
          "X-Error-Report-Signature": signature,
        },
      }
    );
  } catch (err) {
    console.error("Failed to send error report:", err);
  }
};

export const reportError = (
  error: Error | string,
  context?: Record<string, unknown>
): void => {
  const errorObj = typeof error === "string" ? new Error(error) : error;

  const errorReport: ErrorReport = {
    message: errorObj.message,
    stack: errorObj.stack,
    type: "manual",
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    context,
  };

  sendErrorReport(errorReport);
};

export const initializeErrorReporter = (): void => {
  window.onerror = (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error
  ): boolean => {
    if (message === "Script error." && !source && !lineno && !colno && !error) {
      return false;
    }

    const errorReport: ErrorReport = {
      message: typeof message === "string" ? message : "Unknown error",
      stack: error?.stack || `at ${source}:${lineno}:${colno}`,
      type: "runtime",
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      context: {
        source,
        lineno,
        colno,
      },
    };

    sendErrorReport(errorReport);

    return false;
  };

  window.onunhandledrejection = (event: PromiseRejectionEvent): void => {
    const error = event.reason;
    const errorReport: ErrorReport = {
      message:
        error instanceof Error
          ? error.message
          : typeof error === "string"
          ? error
          : "Unhandled Promise Rejection",
      stack: error instanceof Error ? error.stack : undefined,
      type: "unhandled-rejection",
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      context: {
        reason: String(error),
      },
    };

    sendErrorReport(errorReport);
  };
};

export const createReactErrorReporter = () => {
  return async (error: Error, errorInfo: React.ErrorInfo): Promise<void> => {
    const errorReport: ErrorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack || undefined,
      type: "react",
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };

    await sendErrorReport(errorReport);
  };
};

export default {
  reportError,
  initializeErrorReporter,
  createReactErrorReporter,
};
